import styles from './style.module.scss';
import {
    useState,
    useRef,
    useEffect,
    ReactNode,
    Dispatch,
    SetStateAction,
} from 'react';

interface Props {
    value: string;
    onChange: (args: any) => void;
    numInputs: number;
    onChangeRegex?: RegExp;
    labelText?: string;
    classNames?: string;
    autoComplete?: string;
    separator?: ReactNode;
};

const generateDefaultValues = (length: number, inputValues: string[]) => {
    if (length < 1) return [];
    return Array.from({ length }, (_, i) => inputValues[i] || '');
};

const isValid = (regex: RegExp, value: string) => regex.test(value);

const focusOnNextInput = (newValues: string[],
    currentValues: string[], setFocusInput: Dispatch<SetStateAction<number>>) => {
    for (let [i, element] of newValues.entries()) {
        if (!element || i === currentValues.length - 1) {
            setFocusInput(i);
            break;
        }
    }
};

const OTP = (props: Props) => {
    const {
        value,
        onChange,
        numInputs = 4,
        onChangeRegex,
        labelText = 'Enter verification code',
        classNames = 'otp-field',
        autoComplete = 'off',
        separator,
    } = props;

    const defaultValues = generateDefaultValues(numInputs, value.split(''));
    const [values, setValues] = useState(defaultValues);
    const [focusInput, setFocusInput] = useState(0);
    const inputRefs = useRef<HTMLInputElement[]>([]);

    useEffect(() => {
        setValues(defaultValues);
    }, [value, numInputs]);

    useEffect(() => {
        const input = inputRefs.current[focusInput];
        !!input && input.focus();
    }, [focusInput]);

    const handleChange = (inputValue: string, index: number) => {
        if (!!onChangeRegex && !isValid(onChangeRegex, inputValue)) return;

        const newValues = [...values];

        let j = 0;
        values.forEach((element, i) => {
            const isNewValuesAndAnyEmptyInput = !element && !!inputValue;
            const isActionRemoveInputValue = !!element && index === i && !inputValue;

            if (isNewValuesAndAnyEmptyInput) {
                newValues[i] = inputValue.split('')[!values[index] ? j : j + 1] || '';
                j++;
            } else if (isActionRemoveInputValue) {
                newValues[i] = '';
            }
        });

        if (inputValue) {
            focusOnNextInput(newValues, values, setFocusInput);
        }

        onChange(newValues.join(''));
    };

    const onKeyPressed = (key: string, index: number) => {
        switch (key) {
            case 'Backspace':
            case 'ArrowLeft':
                return setFocusInput(index - 1);
            case 'ArrowRight':
                return setFocusInput(index + 1);
            default:
                return;
        }
    };

    return (
        <div className={styles.wrapper}>
            {values.map((element, index) => (
                <div key={index}>
                    <input
                        className={styles.input}
                        ref={el => el && (inputRefs.current[index] = el)}
                        type="tel"
                        value={element}
                        onChange={e => handleChange(e.target.value, index)}
                        aria-label={labelText}
                        aria-required="true"
                        autoComplete={index === 0 ? autoComplete : 'off'}
                        onKeyDown={({ key }) => onKeyPressed(key, index)}
                    />
                    {index !== values.length - 1 && separator && separator}
                </div>
            ))}
        </div>
    );
};

export default OTP;